import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { StripeElementLocale } from '@stripe/stripe-js';
import { useRouter } from 'next/router';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IPaymentOptionFields } from '../../../types/@types/generated/contentful';
import { ImpacterResponse } from '../../../types/ImpacterTypes';
import { FlowType, PaymentType, SubscriptionFlowType } from '../../../types/PaymentTypes';
import {
  ChangeType,
  EVENT_NAMES,
  sendEvent,
  sendSupportDonationChangeEvent,
  trackInitiatePayment,
} from '../../../utils/analytics';
import {
  CategoryResponse,
  Currency,
  getPaymentProviders,
  PaymentProvider,
} from '../../../utils/api';
import CheckoutContext from '../../../utils/checkoutContext';
import { microcopySet, useMicrocopy } from '../../../utils/contentfulMicrocopy';
import { formatAmountAndWithDerivedLocale } from '../../../utils/helpers/currency';
import { useAmountSuggestions } from '../../../utils/hooks/useAmountSuggestions';
import { useCurrencies } from '../../../utils/hooks/useCurrencies';
import { useImpactersData } from '../../../utils/hooks/useImpactersDataArray';
import usePathname from '../../../utils/hooks/usePathname';
import { breakpoints, colors, spacing } from '../../../utils/styleguide';
import { sendGTMEvent } from '../../../utils/tracking/gtm/sendGTMEvent';
import { PaymentFlowTrackingContext } from '../../contentful-elements/contexts/PaymentFlowTrackingContext';
import PrimaryButton from '../buttons/PrimaryButton';
import CurrencyDropDown from '../forms/CurrencyDropDown';
import Switch, { Option } from '../switch/Switch';
import Typography from '../text/Typography';
import AmountSelector from './AmountSelector';
import PaymentFooter from './PaymentFooter';
import StripeCheckout from './StripeCheckout';

const CheckoutContainer = styled.div<{ withBorders: boolean; noOuterBorder: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};
  border: ${({ noOuterBorder }) => (noOuterBorder ? `1px solid ${colors.gray}` : 'none')};
  overflow: hidden;
  width: ${({ withBorders }) => (withBorders ? 'auto' : '100%')};
`;

const AmountSelectorContainer = styled.div<{ withBorders: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${spacing[4]}px ${spacing[3]}px ${spacing[3]}px;

  ${breakpoints.desktop} {
    padding: ${({ withBorders }) =>
      withBorders ? `${spacing[5]}px ${spacing[6]}px ${spacing[4]}px` : `${spacing[3]}px`};
  }
`;

const StripeCheckoutContainer = styled.div<{ show?: boolean; withBorders: boolean }>`
  display: ${(props) => (props.show ? 'block' : 'none')};
  padding: ${spacing[4]}px ${spacing[3]}px;

  ${breakpoints.desktop} {
    padding: ${({ withBorders }) =>
      withBorders ? `${spacing[4]}px ${spacing[6]}px ${spacing[3]}px` : `${spacing[3]}px`};
  }
`;

const DonationAmountClarification = styled.div`
  padding: ${spacing[3]}px;
  background-color: ${colors.brightGray};
  border-top: 1px solid ${colors.gray};

  ${breakpoints.desktop} {
    padding: ${spacing[4]}px ${spacing[6]}px;
  }
`;

const InLineTextButton = styled.span`
  color: ${colors.blueDark};
  cursor: pointer;
`;

type Props = {
  id: string;
  onSuccess?: (obj: { subscriptionId?: string; paymentRequestId?: string }) => void;
  campaign?: string;
  paymentOption?: IPaymentOptionFields;
  projectId?: number;
  impacters?: Pick<ImpacterResponse, 'id' | 'externalId' | 'nickname'>[];
  category?: CategoryResponse;
  locale?: StripeElementLocale;
  variant?: 'with-borders' | 'without-borders';
  noOuterBorder?: boolean;
  colorTheme?: 'default' | 'dark';
};

const StripeCheckoutWithSelector = ({
  id,
  onSuccess,
  campaign,
  paymentOption,
  impacters,
  projectId,
  category,
  locale,
  colorTheme = 'default',
  variant = 'with-borders',
  noOuterBorder = false,
}: Props) => {
  const t = useMicrocopy();
  const router = useRouter();
  const { initiator } = useContext(CheckoutContext);
  const { paymentFlowId, contentfulLandingPageId } = useContext(PaymentFlowTrackingContext);
  const [amount, setAmount] = useState<number>();
  const [showPaymentOptions, setShowPaymentOptions] = useState<boolean>(false);
  const [isCustomAmount, setIsCustomAmount] = useState(false);
  const [option, setOption] = useState<PaymentProvider>();
  const [currency, setCurrency] = useState<Currency>();
  const path = usePathname();
  const initialPaymentType =
    paymentOption?.donationType?.length === 1
      ? paymentOption?.donationType[0]
      : paymentOption?.defaultDonationType || PaymentType.SUBSCRIPTION;
  const [paymentType, setPaymentType] = useState<PaymentType>(initialPaymentType as PaymentType);

  const withBorders = variant === 'with-borders';
  const { currencies, defaultCurrencyCode, isLoading: isLoadingCurrencies } = useCurrencies();
  const { amountSuggestions, defaultAmountSuggestion, isLoadingAmountSuggestions } =
    useAmountSuggestions({
      currency,
      amountConfiguration:
        paymentType === PaymentType.SUBSCRIPTION
          ? paymentOption?.subscriptionAmountConfiguration
          : paymentOption?.oneTimeAmountConfiguration,
    });
  const { impacterIds, impacterPairs } = useImpactersData(impacters);

  useEffect(() => {
    if (!defaultAmountSuggestion || !currency) return;

    // Should be send only once after component rendered with amount and currency data
    sendGTMEvent('productView', {
      currencyCode: currency?.code,
      amount: defaultAmountSuggestion,
      paymentType,
      items: category ? [{ id: category.id, name: category.name }] : impacterPairs,
      donationItemType: category ? 'Category' : impacterPairs.length ? 'Impacter' : undefined,
    });
  }, [currency, defaultAmountSuggestion]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (defaultAmountSuggestion) setAmount(defaultAmountSuggestion);
  }, [amountSuggestions, defaultAmountSuggestion]);

  useEffect(() => {
    let isComponentRendered = true;
    const fetchData = async () => {
      if (isComponentRendered) setOption(undefined);
      if (currency?.code) {
        const options = await getPaymentProviders(currency.code);
        if (isComponentRendered) {
          setOption(options.result.find((option) => option.name === 'stripe'));
        }
      }
    };

    fetchData();

    return () => {
      isComponentRendered = false;
    };
  }, [currency]);

  useEffect(() => {
    setCurrency(currencies?.find((currency) => currency.code === defaultCurrencyCode));
  }, [currencies, defaultCurrencyCode]);

  useEffect(() => {
    trackInitiatePayment({
      flowType: paymentType === PaymentType.ONE_TIME ? FlowType.ONE_TIME : FlowType.SUBSCRIPTION,
      initiator: initiator,
      campaign,
      impacterIds: impacterIds,
      categoryId: category?.id,
      categoryName: category?.name,
      paymentFlowId,
      provider: 'stripe',
    });
  }, [initiator, category, paymentFlowId, impacters, campaign, paymentType, impacterIds]);

  const handleSelectCurrency = useCallback(
    (currencyCode: string) => {
      sendSupportDonationChangeEvent({
        amount,
        currencyCode: currencyCode,
        paymentProvider: option?.name,
        supportType: paymentType === PaymentType.ONE_TIME ? 'one time' : 'subscription',
        path,
        changeType: ChangeType.currency,
        flowType: SubscriptionFlowType.createSubscription,
      });
      if (currencies) {
        setCurrency(currencies.find((currency) => currency.code === currencyCode));
      }
    },
    [amount, currencies, option?.name, paymentType, path],
  );

  const handleOnAmountChange = useCallback(
    ({ amount, isCustomAmount }: { amount: number; isCustomAmount: boolean }) => {
      sendSupportDonationChangeEvent({
        amount,
        currencyCode: currency?.code,
        paymentProvider: option?.name,
        supportType: paymentType === PaymentType.ONE_TIME ? 'one time' : 'subscription',
        path,
        changeType: ChangeType.amount,
        flowType: SubscriptionFlowType.createSubscription,
      });
      setAmount(amount);
      setIsCustomAmount(isCustomAmount);
    },
    [currency?.code, option?.name, paymentType, path],
  );

  /**
   * NOTE: this feature is added to help our dear colleagues out with testing the complete
   * payment flow without having to spend the otherwise minimum amount of ~50SEK each time.
   */
  const testAmount = Number(router.query && router.query['test-custom-amount']);
  const isTestingPayment = Number.isInteger(testAmount);
  const testPaymentAmountOptions = [testAmount, ...(amountSuggestions || [])];
  const amountOptions = isTestingPayment ? testPaymentAmountOptions : amountSuggestions;
  const paymentOptions: [Option, Option] = [
    {
      label: t(microcopySet.PAYMENT_OPTION, 'giveOnce'),
      value: PaymentType.ONE_TIME,
    },
    {
      label: t(microcopySet.PAYMENT_OPTION, 'monthly'),
      value: PaymentType.SUBSCRIPTION,
    },
  ];

  const title = paymentOption?.title ? paymentOption?.title : 'Choose an amount';
  const donationClarification = paymentOption?.donationClarification;

  const buttonLabel = t(microcopySet.PAYMENT_OPTION, 'donate');
  const securePaymentsLabel = t(microcopySet.PAYMENT_OPTION, 'securePayments');
  const termsAndPrivacyCopyright = useMemo(
    () => ({
      donationTerms: t(microcopySet.PAYMENT_OPTION, 'donationTerms'),
      terms: t(microcopySet.PAYMENT_OPTION, 'checkoutTerms'),
      and: t(microcopySet.PAYMENT_OPTION, 'checkoutAnd'),
      privacy: t(microcopySet.PAYMENT_OPTION, 'checkoutPrivacy'),
    }),
    [t],
  );
  const customAmountLabel = paymentOption?.skipCustomAmount
    ? null
    : t(microcopySet.PAYMENT_OPTION, 'otherAmount');
  const editAmountLabel = '(' + t(microcopySet.PAYMENT_OPTION, 'editAmount') + ')';
  const enableDonate = Boolean(currency && option && amount);

  const handleSetPaymentOption = useCallback(
    (newPaymentType) => {
      sendSupportDonationChangeEvent({
        amount,
        currencyCode: currency?.code,
        paymentProvider: option?.name,
        supportType: paymentType === PaymentType.ONE_TIME ? 'one time' : 'monthly',
        path,
        changeType: ChangeType.toggle,
        flowType: SubscriptionFlowType.createSubscription,
      });
      setPaymentType(newPaymentType as PaymentType);
    },
    [amount, currency?.code, option?.name, paymentType, path],
  );

  const handleProceedToPaymentMethod = useCallback(() => {
    sendEvent({
      name: EVENT_NAMES.supportProceedToPaymentMethodSelection,
      properties: {
        amount,
        'currency code': currency?.code,
        'payment provider': option?.name,
        'support type': paymentType === PaymentType.ONE_TIME ? 'one time' : 'monthly',
        path,
        'flow type': SubscriptionFlowType.createSubscription,
      },
    });

    sendGTMEvent('addToCart', {
      currencyCode: currency?.code,
      amount,
      paymentType: paymentType,
      items: category ? [category] : impacterPairs,
      donationItemType: category ? 'Category' : impacterPairs.length ? 'Impacter' : undefined,
    });

    setShowPaymentOptions(true);
  }, [amount, category, currency?.code, impacterPairs, option?.name, paymentType, path]);

  return (
    <>
      <CheckoutContainer id={id} withBorders={withBorders} noOuterBorder={noOuterBorder}>
        {!showPaymentOptions && (
          <AmountSelectorContainer withBorders={withBorders}>
            <Typography
              variant="bodyBold"
              css={css`
                margin-bottom: ${spacing[5]}px;
              `}
            >
              {title}
            </Typography>

            {(!paymentOption?.donationType || paymentOption?.donationType.length === 2) && (
              <Switch
                value={paymentType}
                onChange={handleSetPaymentOption}
                options={paymentOptions}
                css={css`
                  margin-bottom: ${spacing[5]}px;
                `}
              />
            )}

            <AmountSelector
              amountOptions={amountOptions}
              customAmountLabel={customAmountLabel}
              amount={amount}
              isCustomAmount={isCustomAmount}
              onAmountChange={handleOnAmountChange}
              currency={currency}
              css={css`
                width: 100%;
              `}
              showSkeleton={isLoadingAmountSuggestions}
              type={paymentType === PaymentType.ONE_TIME ? 'one time' : 'monthly'}
              colorTheme={colorTheme}
            />

            <CurrencyDropDown
              selectedCurrency={currency?.code}
              currencies={currencies}
              onCurrencySelect={handleSelectCurrency}
              css={css`
                margin-top: ${spacing[1]}px;
                align-self: flex-end;
              `}
              showSkeleton={isLoadingCurrencies}
            />

            <PrimaryButton
              variant="solid"
              color="onLight"
              label={buttonLabel}
              disabled={!enableDonate}
              onClick={handleProceedToPaymentMethod}
              css={css`
                margin: ${spacing[5]}px 0px ${spacing[4]}px;
                width: 100%;
                ${colorTheme === 'dark' &&
                css`
                  color: ${colors.white};
                  background-color: ${colors.black};
                  border-color: ${colors.black};
                  ${enableDonate &&
                  css`
                    :hover {
                      color: ${colors.white};
                      background-color: ${colors.black};
                      border-color: ${colors.black};
                    }
                  `}
                `}
              `}
            />

            {paymentType === PaymentType.SUBSCRIPTION && (
              <Typography
                css={css`
                  margin-bottom: ${spacing[3]}px;
                `}
                variant="detail"
              >
                {t(microcopySet.PAYMENT_OPTION, 'cancelAnytime')}
              </Typography>
            )}
          </AmountSelectorContainer>
        )}

        <StripeCheckoutContainer
          show={Boolean(currency && option && showPaymentOptions)}
          withBorders={withBorders}
        >
          {showPaymentOptions && currency && (
            <Typography
              variant="bodyBold"
              css={css`
                margin-bottom: ${spacing[4]}px;
              `}
            >
              {` ${t(microcopySet.PAYMENT_OPTION, 'giving')} ${
                amount ? formatAmountAndWithDerivedLocale(amount, currency?.code) : ''
              } ${
                paymentType === PaymentType.SUBSCRIPTION
                  ? ` ${t(microcopySet.PAYMENT_OPTION, 'monthly').toLowerCase()}`
                  : ''
              } `}
              <InLineTextButton onClick={() => setShowPaymentOptions(false)}>
                {editAmountLabel}
              </InLineTextButton>
            </Typography>
          )}
          {currency && option && (
            <StripeCheckout
              currencyCode={currency.code}
              amount={amount}
              onSuccess={onSuccess}
              paymentFlowId={paymentFlowId}
              contentfulLandingPageId={contentfulLandingPageId}
              initiator={initiator}
              campaign={campaign}
              publicKey={option.configuration.publishableKey}
              impacters={impacters}
              projectId={projectId}
              category={category}
              variant="dense"
              buttonLabel={buttonLabel}
              enableStripePaymentRequestButton={paymentOption?.enableStripePaymentRequestButton}
              locale={locale}
              paymentType={paymentType}
              skipAmountValidation={isTestingPayment}
              isVisible={Boolean(currency && option && showPaymentOptions)}
              colorTheme={colorTheme}
            />
          )}
        </StripeCheckoutContainer>

        {donationClarification && (
          <DonationAmountClarification>
            <Typography variant="bodySmall">{donationClarification}</Typography>
          </DonationAmountClarification>
        )}
      </CheckoutContainer>
      <PaymentFooter
        securePaymentsLabel={securePaymentsLabel}
        termsAndPrivacyCopyright={termsAndPrivacyCopyright}
      />
    </>
  );
};

export default StripeCheckoutWithSelector;
